import { createSlice } from '@reduxjs/toolkit';

import { foundObjects } from '../../utils/findObjectInNestedArray';
import {
  DEFAULT_BUZZ_CREATOR,
  DEFAULT_CREATOR_COMMENT,
  DEFAULT_CREATOR_RSS_FEED,
  DEFAULT_CREATOR_SUBSCRIPTION,
} from '../../components/Creators/constants/defaultCreators';
import { NAME_CREATORS } from '../../components/Creators/constants/nameCreators';

const INITIAL_STATE = {
  creators: {},
};

export const CreatorsSlice = createSlice({
  name: 'creators',
  initialState: INITIAL_STATE,
  reducers: {
    clearEditor: (state, { payload }) => {
      delete state.creators[payload];
    },
    setBountyCurrency: (state, { payload }) => {
      const { id, bountyCurrency } = payload;

      state.creators[id].bountyCurrency = bountyCurrency;
    },
    setBountyCoins: (state, { payload }) => {
      const { id, bountyCoins } = payload;

      state.creators[id].bountyCoins = bountyCoins;
    },
    setLockedCurrency: (state, { payload }) => {
      const { id, currency } = payload;

      state.creators[id].unlockPrice = currency;
    },
    setLockedCoins: (state, { payload }) => {
      const { id, coins } = payload;

      state.creators[id].lockedCoins = coins;
    },
    setCreator: (state, { payload }) => {
      const { id, type } = payload;

      if (state.creators[id]) return;

      if (type === NAME_CREATORS.COMMENT) {
        state.creators[id] = {
          ...DEFAULT_CREATOR_COMMENT,
          id,
        };
      } else if (type === NAME_CREATORS.RSS_FEED) {
        state.creators[id] = {
          ...DEFAULT_CREATOR_RSS_FEED,
          id,
        };
      } else if (type === NAME_CREATORS.SUBSCRIPTION) {
        state.creators[id] = {
          ...DEFAULT_CREATOR_SUBSCRIPTION,
          id,
        };
      } else {
        state.creators[id] = {
          ...DEFAULT_BUZZ_CREATOR,
          id,
        };
      }
    },
    setCreatorValue: (state, { payload }) => {
      const { id } = payload;

      state.creators[id] = payload;
    },
    setSelectedTagsInput: (state, { payload }) => {
      const { id, value } = payload;
      state.creators[id].selectedTagsInput = value;
    },
    setSelectedTags: (state, { payload }) => {
      const { id, tags } = payload;
      state.creators[id].selectedTags = [...tags];
    },
    setUseCaseInput: (state, { payload }) => {
      const { id, value } = payload;
      state.creators[id].useCaseInput = value;
    },
    setPrivacy: (state, { payload }) => {
      const { id, isPublic } = payload;

      state.creators[id].isPublic = isPublic;
    },
    setIsIgnoreFeedTags: (state, { payload }) => {
      const { id, isIgnoreFeedTags } = payload;

      state.creators[id].isIgnoreFeedTags = isIgnoreFeedTags;
    },
    setIsOpenSource: (state, { payload }) => {
      const { id, isOpenSource } = payload;

      state.creators[id].isOpenSource = isOpenSource;
    },
    setUseCase: (state, { payload }) => {
      const { id, useCase } = payload;
      state.creators[id].content.useCase = useCase;
    },
    setUseCaseTags: (state, { payload }) => {
      const { id, useCaseTags } = payload;

      if (!state.creators[id].content.useCaseTags || !useCaseTags?.length) {
        state.creators[id].content.useCaseTags = [];
      }

      useCaseTags.forEach(newTag => {
        const existingTag = state.creators[id].content.useCaseTags.find(
          tag => tag.id === newTag.id,
        );
        if (!existingTag) {
          state.creators[id].content.useCaseTags.push({ ...newTag });
        }
      });
    },
    setKeyNoteTag: (state, { payload }) => {
      const { id, keyNoteTag } = payload;
      state.creators[id].content.keyNoteTag = keyNoteTag;
    },
    setNoteTags: (state, { payload }) => {
      const { id, noteTags } = payload;
      state.creators[id].content.noteTags = [...noteTags];
    },
    setSnippet: (state, { payload }) => {
      const { id, snippet } = payload;
      const includeMentions = foundObjects(
        snippet,
        'type',
        'mention',
        'character',
      );
      const includeTags = foundObjects(snippet, 'type', 'hashtag', 'character');

      state.creators[id].itemTags = state.creators[id].itemTags.filter(
        itemTag => !includeTags.includes(itemTag),
      );
      state.creators[id].content.mentions = includeMentions;
      state.creators[id].content.tags = includeTags;
      state.creators[id].content.snippet = snippet;
    },
    setSourceContent: (state, { payload }) => {
      const { id, sourceContent } = payload;
      state.creators[id].content.source.content = sourceContent;
    },
    setStructureTags: (state, { payload }) => {
      const { id, structureTags } = payload;

      state.creators[id].structureTags = structureTags;
    },
    setPreSelectUseCaseOptionOfModal: (state, { payload }) => {
      state.creators[payload.id].preSelectUseCaseOptionOfModal =
        payload.useCase;
    },
    setSummary: (state, { payload }) => {
      const { id, summary } = payload;

      state.creators[id].summary = summary;
    },
    setTopics: (state, { payload }) => {
      const { id, tags } = payload;

      state.creators[id].content.topics = tags;
    },
    setTags: (state, { payload }) => {
      const { id, tags } = payload;

      state.creators[id].content.tags = tags;
    },
    setLocations: (state, { payload }) => {
      const { id, tags } = payload;

      state.creators[id].content.locations = tags;
    },
    setTitle: (state, { payload }) => {
      const { id, title } = payload;

      state.creators[id].content.title = title;
    },
    setDescription: (state, { payload }) => {
      const { id, description } = payload;

      state.creators[id].content.description = description;
    },
    setVerified: (state, { payload }) => {
      const { id, isVerified } = payload;

      state.creators[id].moderate = isVerified;
    },
    setIsTitleInColumn: (state, { payload }) => {
      const { id, isTitleInColumn } = payload;

      state.creators[id].isTitleInColumn = isTitleInColumn;
    },
    setPinnedUntil: (state, { payload }) => {
      const { id, pinnedUntil } = payload;

      state.creators[id].pinnedUntil = pinnedUntil;
    },
    setRole: (state, { payload }) => {
      const { id, inputRoleTags } = payload;

      state.creators[id].roles = [
        ...state.creators[id].roles,
        ...inputRoleTags,
      ];
    },
    removeRole: (state, { payload }) => {
      const { id, inputRoleTags } = payload;

      state.creators[id].roles = [...inputRoleTags];
    },
    setCommentPins: (state, { payload }) => {
      const { id, pinned } = payload;

      state.creators[id].pinned = pinned;
    },
    resetCreatorsSlice: () => INITIAL_STATE,
  },
});

export const {
  clearEditor,
  setBountyCurrency,
  setBountyCoins,
  setLockedCurrency,
  setLockedCoins,
  setSelectedTagsInput,
  setSelectedTags,
  setUseCaseInput,
  setUseCaseTags,
  setUseCase,
  setKeyNoteTag,
  setNoteTags,
  setRole,
  removeRole,
  setSnippet,
  setSourceContent,
  setStructureTags,
  setPreSelectUseCaseOptionOfModal,
  setPrivacy,
  setIsIgnoreFeedTags,
  setIsOpenSource,
  setCreator,
  setPinnedUntil,
  setSummary,
  setTitle,
  setDescription,
  setVerified,
  setTopics,
  setTags,
  setLocations,
  setIsTitleInColumn,
  setCreatorValue,
  resetCreatorsSlice,
} = CreatorsSlice.actions;

export default CreatorsSlice.reducer;

export const selectorEditorCreator = id => state => state.creators.creators[id];
export const selectorEditorCreators = state => state.creators.creators;
